import React, { useState } from "react";
import { Button } from "design-system/components/button/button";
import { ButtonSize } from "design-system/components/button/button.types";
import { Icon } from "design-system/components/icon/icon";
import { AudioPlayer } from "design-system/components/audio-player/audio-player";
import { Chip } from "design-system/components/practice/chip/chip";
import type { PracticeSessionData } from "services/api/practice/practiceService.types";
import { PracticeFullscreenImage } from "../practice-fullscreen-image/practice-fullscreen-image";
import { CardLeftSection } from "./card-left";
import { CollapsibleCard } from "./collapsible-card";
import { MCQOptions } from "./mcq-options";
import { PracticeTextContent } from "./practice-text-content";
import {
  CardContainer,
  Divider,
  CardRight,
  ButtonGroup,
  ActionDescription,
  AttemptImage,
  AttemptImageButton,
  QuestionAssetList,
  QuestionAssetButton,
  QuestionAssetImage
} from "./session-card.styles";
import { getButtonState, getQuestionStatus } from "./session-card.utils";
import {
  trackPracticeAudioPlay,
  trackPracticeAudioStreamingStarted,
  trackPracticeAudioStreamingStopped,
  trackPracticeHintOpened,
  trackPracticeHowToSolveOpened
} from "services/api/app-events/practiceEvents";

interface SessionCardProps {
  sessionData: PracticeSessionData;
  accessToken: string;
  onTutorial: () => void;
  onExit: () => void;
  onTakePhoto: () => void;
  onFinish: () => void;
  onNextQuestion: () => void;
  onOpenMcq: () => void;
  onSubmitMcq?: (answerId: string) => void;
  isMcqOpen: boolean;
  blockAudio?: boolean;
}

export const SessionCard: React.FC<SessionCardProps> = ({
  sessionData,
  accessToken,
  onTutorial,
  onExit,
  onTakePhoto,
  onFinish,
  onNextQuestion,
  isMcqOpen,
  onOpenMcq,
  onSubmitMcq,
  blockAudio = false
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [fullscreenImageUrl, setFullscreenImageUrl] = useState("");

  const voiceoverWebsocketUrl = process.env.NEXT_PUBLIC_WEBSOCKET_URL_COMAI;
  const voiceoverClientId = process.env.NEXT_ENV
    ? `practice-${process.env.NEXT_ENV}`
    : undefined;

  const { current_question: question } = sessionData;
  const attemptCount = question.attempts.length;
  const lastAttempt =
    attemptCount > 0 ? question.attempts[attemptCount - 1] : null;
  const lastAttemptCorrect = lastAttempt?.is_correct ?? null;
  const isLastQuestion =
    sessionData.current_question_number >= sessionData.number_of_questions;

  const questionStatus = getQuestionStatus(question.attempts);
  const buttonState = getButtonState(
    attemptCount,
    lastAttemptCorrect,
    isLastQuestion,
    isMcqOpen
  );

  const answerAttempt = lastAttempt?.attempt_number ?? attemptCount;
  const audioSource =
    answerAttempt <= 1 ? "first_answer_analysis" : "second_answer_analysis";
  const handleTutorial = () => {
    trackPracticeHowToSolveOpened(
      accessToken,
      question.id,
      sessionData.current_question_number,
      answerAttempt
    );
    onTutorial();
  };

  const handleHintToggle = (isExpanded: boolean) => {
    if (isExpanded) {
      trackPracticeHintOpened(
        accessToken,
        question.id,
        sessionData.current_question_number,
        answerAttempt
      );
    }
  };

  const getPrimaryButtonContent = () => {
    switch (buttonState) {
      case "TakePhoto":
        return (
          <>
            <Icon name="general-camera" />
            Jawab soal
          </>
        );
      case "TryAgain":
        return (
          <>
            <Icon name="general-camera" />
            Coba lagi
          </>
        );
      case "AnswerWithMcq":
        return "Jawab dengan pilihan ganda";
      case "AnswerMcq":
        return "Jawab soal";
      case "NextQuestion":
        return "Soal berikutnya";
      case "Done":
        return "Selesai";
    }
  };

  const primaryButtonContent = getPrimaryButtonContent();
  const isPrimaryDisabled = buttonState === "AnswerMcq" && selectedId === null;
  const isAnswerWithMcq = buttonState === "AnswerWithMcq";
  const mcqPrompt =
    "Jangan khawatir, Kak Caca juga bisa salah baca tulisanmu! Konfirmasi jawabanmu lewat pilihan ganda, ya.";

  const handlePrimaryButtonClick = () => {
    switch (buttonState) {
      case "TakePhoto":
        return onTakePhoto();

      case "TryAgain":
        return onTakePhoto();

      case "AnswerWithMcq":
        return onOpenMcq();
      case "AnswerMcq":
        if (selectedId) onSubmitMcq?.(selectedId);
        return;
      case "NextQuestion":
        return onNextQuestion();
      case "Done":
        return onFinish();
    }
  };

  const handleExit = () => {
    onExit();
  };

  return (
    <CardContainer>
      <CardLeftSection
        questionTopic={question.topic}
        currentQuestionNumber={sessionData.current_question_number}
        numberOfQuestions={sessionData.number_of_questions}
        questionStatus={questionStatus}
        primaryButtonContent={primaryButtonContent}
        isPrimaryDisabled={isPrimaryDisabled}
        onPrimaryClick={handlePrimaryButtonClick}
        onExit={handleExit}
        onTutorial={handleTutorial}
        actionDescription={isAnswerWithMcq ? mcqPrompt : undefined}
      />

      <Divider />

      <CardRight>
        {/*  === Soal === */}
        <CollapsibleCard
          defaultExpanded={attemptCount === 0}
          header={
            <>
              <Chip
                variant="question"
                icon="general-faq-question"
                label="Soal"
              />
              <Chip
                variant="info"
                label={`Maksimal ${question.max_points} poin kalau benar`}
              />
            </>
          }
        >
          {question.assets.length > 0 && (
            <QuestionAssetList>
              {question.assets.map((asset, index) => (
                <QuestionAssetButton
                  key={`${question.id}-asset-${index}`}
                  type="button"
                  aria-label="Lihat gambar soal dalam ukuran penuh"
                  onClick={() => setFullscreenImageUrl(asset)}
                >
                  <QuestionAssetImage
                    src={asset}
                    alt={`Gambar soal ${index + 1}`}
                  />
                </QuestionAssetButton>
              ))}
            </QuestionAssetList>
          )}
          <PracticeTextContent text={question.title} />
        </CollapsibleCard>

        {/* === Petunjuk === */}
        {question.hint && (
          <CollapsibleCard
            onToggle={handleHintToggle}
            header={
              <Chip variant="hint" icon="general-idea-lamp" label="Petunjuk" />
            }
          >
            <PracticeTextContent text={question.hint} />
          </CollapsibleCard>
        )}

        {/* === Attempt === */}
        {isMcqOpen && (
          <CollapsibleCard
            collapsible={false}
            header={
              <Chip
                variant="info"
                icon="general-brain-star"
                label="Jawabanmu"
              />
            }
          >
            <MCQOptions
              answerList={question.answers}
              selectedAnswer={selectedId}
              onSelect={setSelectedId}
              onImagePress={setFullscreenImageUrl}
            />
          </CollapsibleCard>
        )}
        {!isMcqOpen && lastAttempt?.attempt_type === "mcq" && (
          <CollapsibleCard
            collapsible={false}
            header={
              <Chip
                variant="info"
                icon="general-brain-star"
                label="Jawabanmu"
              />
            }
          >
            <MCQOptions
              answerList={question.answers}
              selectedAnswer={lastAttempt.mcq_selected_answer_id}
              selectedAnswerCorrect={lastAttempt.is_correct}
              onImagePress={setFullscreenImageUrl}
              readOnly
            />
          </CollapsibleCard>
        )}
        {!isMcqOpen && lastAttempt?.attempt_type === "ai" && (
          <CollapsibleCard
            key={lastAttempt.id}
            border={lastAttempt.is_correct ? "correct" : "wrong"}
            collapsible={false}
            header={
              <Chip
                variant={lastAttempt.is_correct ? "correct" : "wrong"}
                icon="general-star-circle"
                label="Pembahasan"
              />
            }
          >
            {lastAttempt.ai_answer_image_url && (
              <AttemptImageButton
                type="button"
                aria-label="Lihat jawaban dalam ukuran penuh"
                onClick={() =>
                  setFullscreenImageUrl(lastAttempt.ai_answer_image_url ?? "")
                }
              >
                <AttemptImage
                  src={lastAttempt.ai_answer_image_url}
                  alt="Gambar jawaban"
                />
              </AttemptImageButton>
            )}

            {lastAttempt.ai_analysis && (
              <AudioPlayer
                voiceUrl={lastAttempt.ai_analysis_voice_url ?? undefined}
                websocketUrl={voiceoverWebsocketUrl}
                accessToken={accessToken}
                blockPlaying={blockAudio}
                messagePayload={
                  voiceoverClientId
                    ? {
                        client_id: voiceoverClientId,
                        text: lastAttempt.ai_analysis,
                        usage: "practice",
                        type: "attempt",
                        id: lastAttempt.id
                      }
                    : undefined
                }
                onAudioPlayed={() =>
                  trackPracticeAudioPlay(
                    accessToken,
                    lastAttempt.id,
                    "practice_attempt",
                    "answer_analysis",
                    audioSource
                  )
                }
                onStreamingStarted={() =>
                  trackPracticeAudioStreamingStarted(
                    accessToken,
                    audioSource,
                    "PracticeSession"
                  )
                }
                onStreamingStopped={(reason, data) =>
                  trackPracticeAudioStreamingStopped(
                    accessToken,
                    audioSource,
                    "PracticeSession",
                    "practice_attempt",
                    lastAttempt.id,
                    reason,
                    data
                  )
                }
              />
            )}

            {lastAttempt.ai_analysis && (
              <PracticeTextContent text={lastAttempt.ai_analysis} />
            )}
          </CollapsibleCard>
        )}
      </CardRight>
      <ButtonGroup $mobile>
        <Button
          appearance="general"
          variant="primary"
          size={ButtonSize.lg}
          isFullWidth
          disabled={isPrimaryDisabled}
          onClick={handlePrimaryButtonClick}
        >
          {primaryButtonContent}
        </Button>
        <Button
          appearance="general"
          variant="secondary"
          size={ButtonSize.lg}
          isFullWidth
          onClick={handleExit}
        >
          Keluar
        </Button>
        {isAnswerWithMcq ? (
          <ActionDescription>{mcqPrompt}</ActionDescription>
        ) : (
          <Button
            appearance="general"
            variant="ghost"
            size={ButtonSize.lg}
            isFullWidth
            onClick={handleTutorial}
          >
            <Icon name="general-info" />
            Cara mengerjakannya
          </Button>
        )}
      </ButtonGroup>
      <PracticeFullscreenImage
        visible={Boolean(fullscreenImageUrl)}
        imageUrl={fullscreenImageUrl}
        onClose={() => setFullscreenImageUrl("")}
      />
    </CardContainer>
  );
};
